import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const UpdatesForm = () => {
  const [version, setVersion] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [isMajor, setIsMajor] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!version.trim() || !title.trim() || !description.trim() || !releaseDate.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('updates')
        .insert({
          version: version.trim(),
          title: title.trim(),
          description: description.trim(),
          release_date: releaseDate,
          is_major: isMajor,
        });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Atualização adicionada com sucesso!",
      });

      // Limpar formulário
      setVersion("");
      setTitle("");
      setDescription("");
      setReleaseDate("");
      setIsMajor(false);
    } catch (error) {
      console.error('Erro ao adicionar atualização:', error);
      toast({
        title: "Erro",
        description: "Não foi possível adicionar a atualização.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Adicionar Nova Atualização
        </CardTitle>
        <CardDescription>
          Adicione uma nova atualização que será exibida na página pública
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="version">Versão *</Label>
              <Input
                id="version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="Ex: 2.6"
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="releaseDate">Data de Lançamento *</Label>
              <Input
                id="releaseDate"
                type="date"
                value={releaseDate}
                onChange={(e) => setReleaseDate(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Melhorias no Sistema de Chat"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva detalhadamente as melhorias e novidades desta atualização..."
              rows={4}
              disabled={isLoading}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isMajor"
              checked={isMajor}
              onCheckedChange={(checked) => setIsMajor(checked as boolean)}
              disabled={isLoading}
            />
            <Label htmlFor="isMajor">Esta é uma versão principal (major)</Label>
          </div>

          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading ? 'Adicionando...' : 'Adicionar Atualização'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default UpdatesForm;